<template>
  <main class="main page home">
    <div class="container">
      <div class="home__layout">
        <!-- LEFT COLUMN: Banner + Feed -->
        <div class="home__main">
          <HomeBanner
            :title="bannerData.title"
            :subtitle="bannerData.subtitle"
            :description="bannerData.description"
            :label="bannerData.label"
            :cta-text="bannerData.ctaText"
            :cta-url="bannerData.ctaUrl"
            :background-image="bannerData.backgroundImage"
            :event-date="bannerData.eventDate"
          />
          <!-- Active filter chips -->
          <div v-if="activeChips.length && !filtersOpen" class="active-filters">
            <div class="active-filters__chips">
              <span
                v-for="chip in activeChips"
                :key="`${chip.category}-${chip.id}`"
                class="active-filters__chip"
              >
                {{ chip.label }}
                <button class="active-filters__chip-x" @click="removeFilter(chip)">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                </button>
              </span>
            </div>
            <button class="active-filters__clear" @click="clearAllFilters">Clear all</button>
          </div>
          <FeedFilters
            :open="filtersOpen"
            :filter-data="filterData"
            :initial-selections="storedSelections"
            :initial-labels="storedSelectionLabels"
            :filters-loading="filtersRefreshing"
            @close="filtersOpen = false"
            @apply="onFiltersApply"
            @filter-changed="onFilterSelectionChanged"
          />
          <HomeFeed
            :articles="feedArticles"
            :tabs="feedTabs"
            :loading="feedLoading"
            :has-more="hasMoreArticles"
            @tab-change="onTabChange"
            @load-more="loadMoreArticles"
            @open-filters="openFilters"
          />
        </div>
        <!-- RIGHT COLUMN: Alert Triage -->
        <div class="home__sidebar">
          <AlertTriage
            @dismiss="onAlertDismiss"
            @action="onAlertAction"
          />
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { mapState } from "vuex";

const FEED_PAGE_SIZE = 15;

export default {
  name: "HomepageV2",
  components: {
    HomeBanner: () => import("./HomeBanner"),
    HomeFeed: () => import("./HomeFeed"),
    FeedFilters: () => import("./FeedFilters"),
    AlertTriage: () => import("./AlertTriage"),
  },
  data() {
    return {
      filtersOpen: false,
      filterData: {},
      baseFilterData: [],
      filtersLoaded: false,
      filtersRefreshing: false,
      filterRefreshTimer: null,
      activeFilterParams: "",
      activeFilterSelections: {},
      bannerData: {
        label: "LIVE WEBINAR",
        title: "Payments Compliance Unburdened: Tackling Regulatory Overload",
        subtitle: "Join our expert panel discussion on managing compliance complexity",
        description: null,
        ctaText: "Find out more",
        ctaUrl: "#",
        backgroundImage: null,
        eventDate: "Thursday 29th January",
      },
      hsOffset: 0,
      hsArticles: [],
      feedLoading: false,
      hasMoreArticles: true,
      displayLimit: FEED_PAGE_SIZE,
    };
  },
  async fetch({ store, $axios, $config }) {
    const apiURL = $config ? $config.apiURL : "http://pc.loc:8005/api/payments_compliance";
    try {
      const { data } = await $axios({
        method: "GET",
        url: `${apiURL}/feed?limit=${FEED_PAGE_SIZE}`,
      });
      store.commit("Main/setData", { label: "insights", data: data.insights || [] });
      store.commit("Main/setData", { label: "regulatoryAnalysis", data: data.analysis || [] });
      store.commit("Main/setData", { label: "horizonScanning", data: data.horizon_scanning || [] });
    } catch (error) {
      console.error("Feed error:", error);
      store.commit("Main/setData", { label: "insights", data: [] });
      store.commit("Main/setData", { label: "regulatoryAnalysis", data: [] });
      store.commit("Main/setData", { label: "horizonScanning", data: [] });
    }
  },
  head() {
    return {
      title: "VIXIO PaymentsCompliance",
    };
  },
  computed: {
    ...mapState("Main", [
      "insights",
      "regulatoryAnalysis",
      "horizonScanning",
    ]),
    ...mapState("Auth", ["subscriptions"]),
    storedSelections() {
      return this.$store.state.FeedFilters.selections || {};
    },
    storedSelectionLabels() {
      return this.$store.state.FeedFilters.selectionLabels || {};
    },
    activeChips() {
      const labels = this.storedSelectionLabels;
      const selections = this.storedSelections;
      const chips = [];
      Object.entries(selections).forEach(([category, tids]) => {
        if (!tids || !tids.length) return;
        tids.forEach((tid) => {
          const key = `${category}:${tid}`;
          chips.push({
            category,
            id: tid,
            label: labels[key] || `${category} ${tid}`,
          });
        });
      });
      return chips;
    },
    feedTabs() {
      return [
        { key: "all", label: "All articles" },
        { key: "analysis", label: "Analysis" },
        { key: "insights", label: "Insights" },
        { key: "horizon_scanning", label: "Horizon Scanning" },
      ];
    },
    feedArticles() {
      const sources = [
        { items: this.insights, type: "Insights" },
        { items: this.regulatoryAnalysis, type: "Analysis" },
        { items: this.horizonScanning, type: "Horizon Scanning" },
        { items: this.hsArticles, type: "Horizon Scanning" },
      ];
      const articles = [];
      sources.forEach(({ items, type }) => {
        if (Array.isArray(items)) {
          items.forEach((item) => {
            articles.push({
              ...item,
              contentType: item.contentType || type,
              publishedDate: this.formatDate(item.publishedDate || item.releasedDate || item.created),
            });
          });
        }
      });
      // Deduplicate by best available identifier
      const seen = new Set();
      let deduped = articles.filter((a) => {
        const key = a.nid || a.nid_export || a.id || a.url || a.relativeLink || a.title;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      // Client-side filter: match selected taxonomy tids against article fields
      // HS items are already filtered server-side, so only apply to insights/analysis
      if (this.hasActiveClientFilters) {
        deduped = deduped.filter((a) => {
          if (a.contentType === "Horizon Scanning") return true;
          return this.matchesFilters(a);
        });
      }
      // Sort by date descending
      deduped.sort((a, b) => {
        const dateA = a.publishedDate || a.releasedDate || a.created || "";
        const dateB = b.publishedDate || b.releasedDate || b.created || "";
        return dateB.localeCompare(dateA);
      });
      return deduped;
    },
    hasActiveClientFilters() {
      return Object.values(this.activeFilterSelections).some((arr) => arr && arr.length > 0);
    },
  },
  mounted() {
    this.hsOffset = FEED_PAGE_SIZE;
    this.restoreFiltersFromStore();
  },
  beforeDestroy() {
    if (this.filterRefreshTimer) {
      clearTimeout(this.filterRefreshTimer);
    }
  },
  methods: {
    commitFeedData(data) {
      if (!data) return;
      if (data.insights) {
        this.$store.commit("Main/setData", { label: "insights", data: data.insights });
      }
      if (data.analysis) {
        this.$store.commit("Main/setData", { label: "regulatoryAnalysis", data: data.analysis });
      }
      if (data.horizon_scanning) {
        this.$store.commit("Main/setData", { label: "horizonScanning", data: data.horizon_scanning });
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    },
    matchesFilters(article) {
      // Map filter category names to article field names
      const categoryFieldMap = {
        jurisdictions: "jurisdictions",
        authorities: "authorities",
        document_types: "documentTypes",
        specialisms: "specialisms",
        specialisms_fs: "specialismsFs",
        products_and_services: "productsAndServices",
        companies: "companies",
        featured_topics: "featuredTopics",
        classifications: "classifications",
        business_model: "businessModels",
      };
      // Every active filter category must have at least one matching tid in the article
      for (const [category, tids] of Object.entries(this.activeFilterSelections)) {
        if (!tids || !tids.length) continue;
        const fieldName = categoryFieldMap[category] || category;
        const articleTaxonomy = article[fieldName];
        if (!Array.isArray(articleTaxonomy) || !articleTaxonomy.length) {
          return false;
        }
        const articleTids = articleTaxonomy.map((t) => t.tid || t.id || t);
        const hasMatch = tids.some((tid) => articleTids.includes(tid));
        if (!hasMatch) return false;
      }
      return true;
    },
    onTabChange(tab) {
      if (this.$matomo) {
        this.$matomo.trackEvent("Homepage", "Feed tab change", tab);
      }
    },
    async openFilters() {
      this.filtersOpen = !this.filtersOpen;
      if (this.filtersOpen && !this.filtersLoaded) {
        await this.loadFilterData();
      }
    },
    async loadFilterData() {
      try {
        const { data } = await this.$store.dispatch("Main/getViewData", {
          url: "/filters/horizon-scanning",
        });
        if (data) {
          this.filterData = data;
          this.baseFilterData = JSON.parse(JSON.stringify(data));
          this.filtersLoaded = true;
        }
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
      }
    },
    onFilterSelectionChanged({ effectiveSelections }) {
      if (this.filterRefreshTimer) {
        clearTimeout(this.filterRefreshTimer);
      }
      const sel = effectiveSelections;
      this.filterRefreshTimer = setTimeout(() => {
        this.refreshFilterOptions(sel);
      }, 300);
    },
    async refreshFilterOptions(selections) {
      const parts = [];
      Object.entries(selections || {}).forEach(([category, tids]) => {
        if (tids && tids.length) {
          tids.forEach((tid) => {
            parts.push("filter[" + encodeURIComponent(category) + "][]=" + encodeURIComponent(tid));
          });
        }
      });
      if (!parts.length) {
        this.filterData = JSON.parse(JSON.stringify(this.baseFilterData));
        return;
      }
      this.filtersRefreshing = true;
      try {
        const { data } = await this.$store.dispatch("Main/getViewData", {
          url: "/filters/horizon-scanning?" + parts.join("&"),
        });
        if (data && Array.isArray(data)) {
          this.filterData = this.mergeFilterData(this.baseFilterData, data);
        }
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
      }
      this.filtersRefreshing = false;
    },
    mergeFilterData(base, faceted) {
      if (!Array.isArray(base) || !base.length) return faceted;
      const facetedMap = {};
      (faceted || []).forEach((group) => {
        facetedMap[group.category] = group;
      });
      return base.map((baseGroup) => {
        const match = facetedMap[baseGroup.category];
        return Object.assign({}, baseGroup, {
          children: match ? match.children : [],
        });
      });
    },
    async onFiltersApply({ queryParams, selections, rawSelections, rawLabels }) {
      this.activeFilterParams = queryParams;
      this.activeFilterSelections = selections || {};
      this.filtersOpen = false;
      this.$store.commit("FeedFilters/setSelections", {
        selections: rawSelections || {},
        selectionLabels: rawLabels || {},
        effectiveSelections: selections || {},
        queryParams: queryParams || "",
      });
      this.hsArticles = [];
      this.hsOffset = 0;
      this.hasMoreArticles = true;
      this.displayLimit = FEED_PAGE_SIZE;
      this.feedLoading = true;
      try {
        const filterSuffix = queryParams ? `&${queryParams}` : "";
        const apiURL = this.$config ? this.$config.apiURL : "http://pc.loc:8005/api/payments_compliance";
        const { data } = await this.$axios({
          method: "GET",
          url: `${apiURL}/feed?offset=0&limit=${FEED_PAGE_SIZE}${filterSuffix}`,
        });
        if (data) {
          this.commitFeedData(data);
          this.hsOffset = FEED_PAGE_SIZE;
          if (!data.horizon_scanning || data.horizon_scanning.length < FEED_PAGE_SIZE) {
            this.hasMoreArticles = false;
          }
        }
      } catch (error) {
        console.error(error);
      }
      this.feedLoading = false;
    },
    async loadMoreArticles() {
      if (this.feedLoading || !this.hasMoreArticles) return;
      this.displayLimit += FEED_PAGE_SIZE;
      this.feedLoading = true;
      try {
        const filterSuffix = this.activeFilterParams ? `&${this.activeFilterParams}` : "";
        const apiURL = this.$config ? this.$config.apiURL : "http://pc.loc:8005/api/payments_compliance";
        const { data } = await this.$axios({
          method: "GET",
          url: `${apiURL}/feed?offset=${this.hsOffset}&limit=${FEED_PAGE_SIZE}${filterSuffix}`,
        });
        const hs = data && data.horizon_scanning ? data.horizon_scanning : [];
        if (hs.length > 0) {
          this.hsArticles = [...this.hsArticles, ...hs];
          this.hsOffset += FEED_PAGE_SIZE;
          if (hs.length < FEED_PAGE_SIZE) {
            this.hasMoreArticles = false;
          }
        } else {
          this.hasMoreArticles = false;
        }
      } catch (error) {
        console.error(error);
        this.hasMoreArticles = false;
      }
      this.feedLoading = false;
    },
    async restoreFiltersFromStore() {
      let stored = this.$store.state.FeedFilters;

      // Vuex may be empty after SSR — check localStorage directly
      if (!stored || !Object.keys(stored.selections).length) {
        try {
          const raw = localStorage.getItem("feedFilters");
          if (raw) {
            stored = JSON.parse(raw);
            // Hydrate the store so FeedFilters component can read it
            this.$store.commit("FeedFilters/setSelections", stored);
          }
        } catch (e) {
          // ignore
        }
      }

      if (!stored || !Object.keys(stored.selections).length) return;

      this.activeFilterSelections = stored.effectiveSelections || {};
      this.activeFilterParams = stored.queryParams || "";

      if (!this.activeFilterParams) return;

      // Re-fetch with stored filter params
      this.hsArticles = [];
      this.hsOffset = 0;
      this.hasMoreArticles = true;
      this.displayLimit = FEED_PAGE_SIZE;
      this.feedLoading = true;
      try {
        const apiURL = this.$config ? this.$config.apiURL : "http://pc.loc:8005/api/payments_compliance";
        const { data } = await this.$axios({
          method: "GET",
          url: `${apiURL}/feed?offset=0&limit=${FEED_PAGE_SIZE}&${this.activeFilterParams}`,
        });
        if (data) {
          this.commitFeedData(data);
          this.hsOffset = FEED_PAGE_SIZE;
          if (!data.horizon_scanning || data.horizon_scanning.length < FEED_PAGE_SIZE) {
            this.hasMoreArticles = false;
          }
        }
      } catch (error) {
        console.error(error);
      }
      this.feedLoading = false;
    },
    removeFilter(chip) {
      const stored = JSON.parse(JSON.stringify(this.$store.state.FeedFilters));
      if (stored.selections[chip.category]) {
        stored.selections[chip.category] = stored.selections[chip.category].filter((t) => t !== chip.id);
        if (!stored.selections[chip.category].length) {
          delete stored.selections[chip.category];
        }
      }
      delete stored.selectionLabels[`${chip.category}:${chip.id}`];
      // Rebuild effective selections and query params — re-apply via the same flow
      this.$store.commit("FeedFilters/setSelections", {
        selections: stored.selections,
        selectionLabels: stored.selectionLabels,
        effectiveSelections: stored.selections, // simplified — parent removed means children gone
        queryParams: this.buildQueryParams(stored.selections),
      });
      this.activeFilterSelections = stored.selections;
      this.activeFilterParams = this.buildQueryParams(stored.selections);
      this.refetchWithFilters();
    },
    clearAllFilters() {
      this.$store.commit("FeedFilters/clearSelections");
      this.activeFilterSelections = {};
      this.activeFilterParams = "";
      this.refetchWithFilters();
    },
    buildQueryParams(selections) {
      const parts = [];
      Object.entries(selections || {}).forEach(([category, tids]) => {
        if (tids && tids.length) {
          tids.forEach((tid) => {
            parts.push(`filter[${encodeURIComponent(category)}][]=${encodeURIComponent(tid)}`);
          });
        }
      });
      return parts.join("&");
    },
    async refetchWithFilters() {
      this.hsArticles = [];
      this.hsOffset = 0;
      this.hasMoreArticles = true;
      this.displayLimit = FEED_PAGE_SIZE;
      this.feedLoading = true;
      try {
        const filterSuffix = this.activeFilterParams ? `&${this.activeFilterParams}` : "";
        const apiURL = this.$config ? this.$config.apiURL : "http://pc.loc:8005/api/payments_compliance";
        const { data } = await this.$axios({
          method: "GET",
          url: `${apiURL}/feed?offset=0&limit=${FEED_PAGE_SIZE}${filterSuffix}`,
        });
        if (data) {
          this.commitFeedData(data);
          this.hsOffset = FEED_PAGE_SIZE;
          if (!data.horizon_scanning || data.horizon_scanning.length < FEED_PAGE_SIZE) {
            this.hasMoreArticles = false;
          }
        }
      } catch (error) {
        console.error(error);
      }
      this.feedLoading = false;
    },
    onAlertDismiss(index) {
      // No-op for now
    },
    onAlertAction(index) {
      // No-op for now
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../assets/styles/helpers/variables";
@import "../../assets/styles/helpers/mixin";

.home {
  padding-top: 32px;

  &__layout {
    display: flex;
    gap: 32px;

    @include below($md) {
      flex-direction: column;
      gap: 24px;
    }
  }

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__sidebar {
    width: 340px;
    flex-shrink: 0;

    @include below($lg) {
      width: 300px;
    }

    @include below($md) {
      width: 100%;
    }
  }
}

.active-filters {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 0;
  margin-bottom: 8px;
  border-bottom: 1px solid #ecedf0;

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    flex: 1;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    background: rgba($corporateBlue, 0.06);
    color: $corporateBlue;
    border-radius: 4px;
    font-size: 1.15rem;
    font-weight: 600;
    white-space: nowrap;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__chip-x {
    display: inline-flex;
    align-items: center;
    background: none;
    border: none;
    cursor: pointer;
    color: $corporateBlue;
    padding: 0;
    opacity: 0.5;
    transition: opacity 0.12s ease;
    flex-shrink: 0;

    &:hover {
      opacity: 1;
    }
  }

  &__clear {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.15rem;
    font-weight: 600;
    color: $light_gray;
    white-space: nowrap;
    padding: 4px 0;
    transition: color 0.12s ease;
    flex-shrink: 0;

    &:hover {
      color: $text;
    }
  }
}
</style>
